import { useLingui } from "@lingui/react";
import { observer } from "mobx-react-lite";
import { Suspense, useEffect, useMemo, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";

import { Spinner } from "../../components/ui-kit/spinner";
import { devTools } from "../../config/config";
import { hasAIChatAccess } from "../../constants/permissions";
import { ChatBubble } from "../../features/chat/chat-bubble";
import { DevTools } from "../../features/dev-tools/dev-tools";
import { AppModel } from "../app-model";
import { Footer } from "../footer";
import { PasswordPrompt } from "../password-prompt";
import { BackButton } from "./back-button";
import { BreadcrumbsTitle } from "./breadcrumbs-title";
import { DocumentTitle } from "./document-title";
import { LayoutProvider } from "./layout-context";
import { MainHeader } from "./main-header";
import { MenuList } from "./menu/menu-list";
import { buildMenu, getActiveSubmenu } from "./menu/nav-menu";
import { Sidebar } from "./sidebar/sidebar";

export const MainLayout = observer(function MainLayout(props: {
  app: AppModel;
}) {
  const [headerActionsNode, setHeaderActionsNode] =
    useState<HTMLElement | null>(null);

  const layoutContextValue = useMemo(
    () => ({ headerActionsNode }),
    [headerActionsNode],
  );

  const { i18n } = useLingui();
  const pathname = useLocation().pathname;
  const sidebarMenu = getActiveSubmenu(
    buildMenu(props.app, pathname, i18n),
    pathname,
  );

  useEffect(() => {
    void props.app.seerlinqApi.assumeLoggedIn();
  }, [props.app.seerlinqApi]);

  return (
    <>
      {props.app.seerlinqApi.promptPassword && (
        <PasswordPrompt api={props.app.seerlinqApi} />
      )}

      {props.app.seerlinqApi.loggedIn ? (
        <LayoutProvider value={layoutContextValue}>
          <DocumentTitle app={props.app} />
          <MainHeader app={props.app} />
          <div className="grow flex">
            <Sidebar>
              {sidebarMenu && (
                <MenuList items={sidebarMenu} variant="sidebar" />
              )}
            </Sidebar>
            <div className="grow flex flex-col pl-4 nav:pl-[60px] min-w-0 pr-4">
              <main className="grow mb-12">
                <div className="flex items-start justify-between mb-9 mt-9">
                  <BreadcrumbsTitle className="mb-0 mt-0" />
                  {/* Flex row, not a bare div: a route can put several controls
                      in the header (e.g. an assigned-only toggle beside a
                      search trigger), and they must sit inline rather than
                      stack. */}
                  <div
                    className="flex items-center gap-4"
                    ref={setHeaderActionsNode}
                  />
                </div>

                <BackButton />

                {/* Catalog hooks (e.g. useSupportedDiagnoses) suspend until
                    their reference data loads; this boundary keeps that loading
                    out of leaf components across every page. */}
                <Suspense
                  fallback={
                    <div className="flex h-full items-center justify-center">
                      <Spinner />
                    </div>
                  }
                >
                  <Outlet />
                </Suspense>
              </main>

              <Footer app={props.app} />
            </div>
          </div>
          {hasAIChatAccess(props.app.seerlinqApi.permissions) && (
            <ChatBubble api={props.app.seerlinqApi} />
          )}
          {devTools && (
            <DevTools
              open={props.app.devToolsOpen}
              onOpenChange={(open) => props.app.setDevToolsOpen(open)}
              api={props.app.seerlinqApi}
            />
          )}
        </LayoutProvider>
      ) : (
        <div className="grow flex items-center justify-center">
          <Spinner />
        </div>
      )}
    </>
  );
});
